import styles from './accordion.scss?inline';
import { LitElement, html, unsafeCSS, CSSResult } from 'lit';
import { property, queryAssignedElements, query, state } from 'lit/decorators.js';
import { literal } from 'lit/static-html.js';
import { C11nPrefix, C11nPrefixLiteral } from 'virtual:c11n-prefix';
import { AccordionHeaderComponent } from './accordion-header';
import { c11nElement } from '../core';

@c11nElement()
export class AccordionComponent extends LitElement {
  static selector = `${C11nPrefix}-accordion`;
  static literal = literal`${C11nPrefixLiteral}-accordion`;
  static override styles: CSSResult = unsafeCSS(styles);
  
  @property({ attribute: 'open', type: Boolean, reflect: true })
  _open: boolean = false;
  
  @property({ attribute: 'animation-disabled', type: Boolean })
  animationDisabled: boolean = false;
  
  @property({ attribute: 'c11n-web-accordion', type: Boolean, reflect: true })
  readonly _c11n_web_accordion: boolean = true;
  
  @queryAssignedElements({ slot: 'header' })
  headerEls!: Array<HTMLHeadingElement>;
  
  @query('div.c11n-accordion__body__content')
  bodyContentEl!: HTMLDivElement;
  
  @query('div.c11n-accordion__body')
  bodyEl!: HTMLDivElement;

  @state()
  height: string = '0';
  
  get headerEl() {
    return this.headerEls && this.headerEls[0];
  }
  
  get accordionHeaderComponent(): AccordionHeaderComponent {
    return this.headerEl?.querySelector('[c11n-web-accordion-header]') as AccordionHeaderComponent;
  }
  
  accordionVarSheet: CSSStyleSheet = new CSSStyleSheet();

  override connectedCallback(): void {
    super.connectedCallback();
    this.shadowRoot?.adoptedStyleSheets.push(this.accordionVarSheet);
  }
  
  override firstUpdated() {
    this.updateHeaderOpenState();
  }
  
  override updated(changedProperties: Map<any, any>): void {
    if (changedProperties.has('_open')) {
      if (this.animationDisabled) {
        this.height = this._open ? 'auto' : '0';
        this._open ? this.accordionHeaderComponent?.setAttribute('open', '') : this.accordionHeaderComponent?.removeAttribute('open');  
      } else {
        this._open ? this.openAccordion() : this.closeAccordion();
      }
      if (this.accordionHeaderComponent?.hasAttribute('open') !== this._open) {
        this.updateHeaderOpenState();
      }
      const eventType = this._open ? 'open' : 'close';
      this.dispatchEvent(new CustomEvent(eventType, { bubbles: true, composed: true, detail: { accordionId: this.id, isOpen: this._open } }));  
    }
  }

  updateHeaderOpenState() {
    setTimeout(() => {
      this._open ? this.accordionHeaderComponent?.setAttribute('open', '') : this.accordionHeaderComponent?.removeAttribute('open');
    },0);
  }

  filterAnimations(animations: Animation[], cssProperty: string) {
    return animations.find(animation => {
      if (!animation.effect || !(animation.effect instanceof KeyframeEffect)) {
        return false;
      }
      const keyframes = animation.effect.getKeyframes();
      if (keyframes.length < 1) {
        return false;
      }
      return keyframes.some(keyframe => cssProperty in keyframe);
    }) || null;
  }

  handleError(e: Error): void {
    if (e.name !== 'AbortError') {
      this._open = false;
      this.height = '0';
    }
  }

  openAccordion() {
    this.accordionHeaderComponent?.setAttribute('open', '');
    this.height = `${this.bodyContentEl.offsetHeight}px`;
    requestAnimationFrame(() => {
      const animations = this.bodyEl.getAnimations();
      const heightAnimation = this.filterAnimations(animations, 'height');
      if (heightAnimation) {
        heightAnimation.finished
        .then(() => {
          this.height = 'auto';
        })
        .catch((e) => {
          this.handleError(e);
        });
      }
    });
  }

  closeAccordion() {
    this.accordionHeaderComponent?.removeAttribute('open');
    this.height = `${this.bodyContentEl.offsetHeight}px`;
    requestAnimationFrame(() => {
      this.height = '0';
      const animations = this.bodyEl.getAnimations();
      const heightAnimation = this.filterAnimations(animations, 'height');
      if (heightAnimation) {
        heightAnimation.finished
        .catch((e) => {
          this.handleError(e);
        });
      }
    });
  }

  toggleAccordion() {
    this._open = !this._open;
  }

  override render() {

    this.accordionVarSheet.replaceSync(`
      :host {
        --height: ${this.height};
      }
    `);

    return html`
      <slot name="header" @click=${this.toggleAccordion}></slot>
      <div class="c11n-accordion__body">
        <div class="c11n-accordion__body__content">
          <slot></slot>
        </div>
      </div>
    `;
  }
}

export const C11nAccordion = AccordionComponent.selector;
export const C11nAccordionLiteral = AccordionComponent.literal;
